import { api } from '@/api'
import type {
  AcceptInvitationPayload,
  AuthSession,
  ChangePasswordPayload,
  InvitationPreview,
  LoginPayload,
  MagicLinkRequestPayload,
  MeResponse,
  UpdateMePayload,
  User,
} from '@/api/types'

export const authApi = {
  login: (payload: LoginPayload) => api.post<AuthSession>('/auth/login', payload, { auth: false }),

  logout: async (refreshToken: string | null, all = false): Promise<void> => {
    await api.post('/auth/logout', { refresh_token: refreshToken, all })
  },

  me: () => api.get<MeResponse>('/auth/me'),

  updateMe: (payload: UpdateMePayload) => api.patch<User>('/auth/me', payload),

  /**
   * Tolère les deux formes de réponse, le temps de la bascule du contrat 1.18 :
   * un 204 aujourd'hui, un couple de jetons neufs ensuite. Rendre `null` sur
   * l'ancienne forme laisse l'appelant décider, et le jour où le corps arrive
   * la session courante est simplement renouvelée — sans fenêtre pendant
   * laquelle l'écran serait faux dans un sens ou dans l'autre.
   */
  changePassword: async (payload: ChangePasswordPayload): Promise<AuthSession | null> => {
    const session = await api.post<AuthSession | null>('/auth/change-password', payload)
    return session && 'access_token' in session ? session : null
  },

  invitationPreview: (token: string) =>
    api.get<InvitationPreview>(`/auth/invitations/${encodeURIComponent(token)}`, { auth: false }),

  /** 1.69.0 : un lien de connexion par courriel, pour un compte portail actif. */
  requestMagicLink: async (payload: MagicLinkRequestPayload): Promise<void> => {
    await api.post('/auth/magic-link', payload, { auth: false })
  },

  /** 401 `magic_link_invalid` : inconnu, expiré ou déjà utilisé — le même refus. */
  consumeMagicLink: (token: string) =>
    api.post<AuthSession>('/auth/magic-link/consume', { token }, { auth: false }),

  /** 1.69.0 : poser un mot de passe quand on n'en a pas (409 `password_already_set`). */
  setPassword: async (newPassword: string): Promise<void> => {
    await api.post('/auth/set-password', { new_password: newPassword })
  },

  acceptInvitation: (token: string, payload: AcceptInvitationPayload) =>
    api.post<AuthSession>(`/auth/invitations/${encodeURIComponent(token)}/accept`, payload, {
      auth: false,
    }),
}
